/**
 * The ⌘K palette's **records** source — the demo's `INDEX` search.
 *
 * The demo's ⌘K searched twenty kinds of record, including the bodies of every
 * email, meeting and video transcript. Machines (`fleet/palette.ts`), lake
 * files (`integration/documents-palette.ts`) and pages (navigation) already
 * have their own groups in the palette, so this source carries the other
 * eighteen kinds, ranked by the demo's own scorer.
 *
 * ## Lazy twice over
 *
 * `PaletteSource.search` must be synchronous, and the index reads six lazy
 * domains. So the first `search()` call — which the palette makes the moment
 * it opens — kicks off a dynamic `import()` of `shared/search/oem-index.ts`
 * and its one-off build. Until that lands the source reports `pending`, and
 * the palette says "Indexing records…" rather than "Nothing matches". When it
 * lands the source re-registers, which re-runs an open palette's query.
 *
 * Nothing here is on the first-paint path: the index module is its own chunk
 * and the domains are fetched only when someone opens ⌘K.
 *
 * Registered at module scope; `OemShell` imports this file for the side
 * effect, so the source is live on every route.
 *
 * @see src/versions/v1/shared/search/oem-index.ts — the index and the scorer
 * @see src/versions/v1/chrome-oem/OemCommandPalette.tsx
 */

import { registerPaletteSource, type PaletteResult } from './OemCommandPalette';
import type { OemSearchItem, OemSearchKind } from '../shared/search/oem-index';

/** Kinds that already have their own palette group. */
const OWN_GROUP: ReadonlySet<OemSearchKind> = new Set<OemSearchKind>(['Machine', 'File']);

type IndexModule = typeof import('../shared/search/oem-index');

let items: readonly OemSearchItem[] | null = null;
let search: IndexModule['searchOemIndex'] | null = null;
let loading = false;

function toResult(item: OemSearchItem, index: number): PaletteResult {
  return {
    id: `${item.kind}:${index}:${item.title}`,
    kind: item.kind,
    label: item.title,
    detail: item.sub || undefined,
    hint: item.meta || undefined,
    to: item.target.to,
    slice: item.target.slice,
    toast: item.target.toast,
  };
}

/** Load the index module and build the index, once. */
function warm(): void {
  if (items || loading) return;
  loading = true;
  import('../shared/search/oem-index')
    .then((mod) =>
      mod.loadOemIndex().then((built) => {
        items = built.filter((item) => !OWN_GROUP.has(item.kind));
        search = mod.searchOemIndex;
      }),
    )
    .catch(() => {
      // Leave the source empty; the next palette open retries.
    })
    .finally(() => {
      loading = false;
      register();
    });
}

function register(): void {
  registerPaletteSource({
    id: 'records',
    label: 'Records',
    // Below machines (an id is the most specific thing to type), above lake
    // files and pages.
    order: 20,
    pending: () => loading,
    search: (query) => {
      warm();
      if (!items || !search || !query) return [];
      return search(items, query).map((hit, i) => toResult(hit.item, i));
    },
  });
}

register();
